/**
 * Extracts a single human-readable message from a DRF-style error payload.
 * Handles: {detail: "..."}, {error: "..."}, and field validation errors
 * like {title: ["This field is required."], category: ["..."]}.
 */
export const extractErrorMessage = (errorData: any, fallback = 'API request failed'): string => {
  if (!errorData || typeof errorData !== 'object') {
    return fallback;
  }

  if (typeof errorData.detail === 'string') {
    return errorData.detail;
  }

  if (typeof errorData.error === 'string') {
    return errorData.error;
  }

  const keys = Object.keys(errorData);
  if (keys.length > 0) {
    const firstVal = errorData[keys[0]];
    if (Array.isArray(firstVal) && firstVal.length > 0 && typeof firstVal[0] === 'string') {
      return firstVal[0];
    }
    if (typeof firstVal === 'string') {
      return firstVal;
    }
  }

  return fallback;
};

/**
 * Parses a failed fetch Response into a normalized Error. Falls back to
 * response.statusText if the body isn't valid JSON.
 */
export const parseErrorResponse = async (response: Response, fallback = 'API request failed'): Promise<Error> => {
  try {
    const errorData = await response.json();
    return new Error(extractErrorMessage(errorData, fallback));
  } catch (e) {
    return new Error(response.statusText || fallback);
  }
};
